import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { AuthShell } from "@/components/auth-shell";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { saveOnboardingProgress } from "@/lib/onboarding.functions";

export const Route = createFileRoute("/welcome")({
  head: () => ({
    meta: [
      { title: "Xamseen — Welcome" },
      { name: "description", content: "Your community starts here. Set up your Xamseen account and find the people and places near you." },
      { property: "og:title", content: "Welcome to Xamseen" },
      { property: "og:description", content: "Your community starts here." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WelcomePage,
});

function WelcomePage() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const saveProgress = useServerFn(saveOnboardingProgress);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/auth" });
  }, [loading, user, navigate]);

  // Returning members never see the new-member welcome.
  useEffect(() => {
    if (profile?.onboarded) navigate({ to: "/" });
  }, [profile?.onboarded, navigate]);

  if (loading || !user) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const first =
    profile?.first_name || (profile?.full_name ? profile.full_name.split(" ")[0] : null);

  return (
    <AuthShell
      title={first ? `Welcome to Xamseen, ${first}.` : "Welcome to Xamseen."}
      subtitle="Your community starts here."
      footer={
        <button
          type="button"
          className="hover:underline"
          onClick={async () => {
            // Record that the welcome was seen so the member is not sent back
            // here on every page load; onboarding stays available in Settings.
            try {
              await saveProgress({ data: { step: 1 } });
            } catch (e) {
              // Never trap someone on this screen if the write fails.
              console.error("welcome skip save failed", e);
            }
            navigate({ to: "/" });
          }}
        >
          Skip for now
        </button>
      }
    >
      <p className="text-[15px] leading-relaxed text-muted-foreground">
        In the next couple of minutes you can tell us where you're based and what you care about, so
        your feed shows what's actually happening around you. You can change any of it later.
      </p>
      <Button
        className="mt-8 h-11 w-full font-medium"
        disabled={leaving}
        onClick={() => {
          setLeaving(true);
          navigate({ to: "/onboarding" });
        }}
      >
        {leaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Continue
      </Button>
    </AuthShell>
  );
}
